// web/src/admin/AdminReview.tsx — /admin/review (portal spec 22, X11): monthly runs in `review`. A site's first three
// runs wait here (sites.kc_review_runs_left > 0). Each card: the draft report (spec 17's ReportView), the suggestions
// (class, title, why, commit count), the preview link and how many reviews are left. Release = spec 20's route (the
// client then sees the report and the ideas; the counter goes down by one). Cancel = spec 20's cancel (the run ends held).
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminProposal, UpdateRun } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Skeleton } from '../components/Skeleton';
import { ReportView } from '../components/ReportView';
import { Icon } from '../components/Icon';
import { Btn, Collapsible, Empty, ago, fmtDate, useAction } from './adminUi';

const CLASS: Record<'green' | 'yellow' | 'red', { label: string; tone: string }> = {
  green: { label: 'Done', tone: 'bg-success/15 text-success' },
  yellow: { label: 'Needs a yes', tone: 'bg-warn/15 text-warn' },
  red: { label: 'Let’s talk', tone: 'bg-danger/10 text-danger' },
};

export function reviewsLeftLine(left: number): string {
  const after = Math.max(0, left - 1);
  if (left <= 0) return 'Reviews are off for this site: its runs release on their own.';
  if (after === 0) return 'This is the last review for this site, then automatic.';
  return `${after} more review${after === 1 ? '' : 's'} for this site, then automatic.`;
}

export function AdminReview() {
  const runs = useQuery({ queryKey: ['admin-runs'], queryFn: () => api.adminUpdateRuns(200) });
  const queue = useQuery({ queryKey: ['admin-proposals-review'], queryFn: () => api.adminProposalsReview() });
  const sites = useQuery({ queryKey: ['admin-sites'], queryFn: () => api.adminSites() });
  const inReview = (runs.data ?? []).filter((r) => r.status === 'review');
  const runIds = new Set(inReview.map((r) => r.id));
  const orphans = (queue.data ?? []).filter((p) => !p.updateRunId || !runIds.has(p.updateRunId));
  const left = useMemo(() => new Map((sites.data ?? []).map((s) => [s.id, s.kcReviewRunsLeft])), [sites.data]);
  return (
    <>
      <PageHeader title="Review" subtitle="Each site's first three monthly reports and ideas wait for you here before the client sees them." />
      {runs.isLoading && <Skeleton variant="card" count={2} />}
      {runs.data && inReview.length === 0 && <Empty>Nothing to review.</Empty>}
      <div className="flex min-w-0 flex-col gap-4">
        {inReview.map((r) => <ReviewCard key={r.id} r={r} left={left.get(r.siteId)} />)}
      </div>
      {orphans.length > 0 && (
        <div className="mt-5">
          <Collapsible title="Suggestions in review without a run waiting" count={orphans.length}>
            <ul className="flex flex-col gap-2 text-sm">
              {orphans.map((p) => (
                <li key={p.id} className="rounded-md border border-hair p-3">
                  <Link className="text-ink underline" to={`/admin/clients/${encodeURIComponent(p.slug)}`}>{p.siteName}</Link> · {p.period} · {p.green} done, {p.yellow} to answer, {p.red} to talk about
                  <div className="text-xs text-muted">They're released with their monthly run. Posted {ago(p.createdAt)}.</div>
                </li>
              ))}
            </ul>
          </Collapsible>
        </div>
      )}
    </>
  );
}

function ReviewCard({ r, left }: { r: UpdateRun; left: number | undefined }) {
  const { busy, run } = useAction();
  const [confirm, setConfirm] = useState<'release' | 'cancel' | null>(null);
  const report = useQuery({ queryKey: ['admin-report', r.reportId], queryFn: () => api.adminReport(r.reportId!), enabled: !!r.reportId });
  const prop = useQuery({ queryKey: ['admin-proposal', r.proposalId], queryFn: () => api.adminProposal(r.proposalId!), enabled: !!r.proposalId });
  const preview = prop.data?.previewUrl;
  return (
    <article className="min-w-0 rounded-lg border border-hair bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/admin/clients/${encodeURIComponent(r.slug ?? '')}`} className="inline-flex min-h-11 items-center break-words font-head text-ink underline">{r.siteName ?? r.slug}</Link>
          <div className="text-xs text-muted">{r.period} · run posted {ago(r.updatedAt)} · ${r.costUsd} of ${r.costCapUsd}</div>
        </div>
        {preview && <a href={preview} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm text-ink underline">See the preview<Icon name="external" size={13} /></a>}
      </div>
      {left !== undefined && <p className="mt-2 text-sm text-muted">{reviewsLeftLine(left)}</p>}

      <h3 className="mt-4 font-head text-sm text-muted">The report</h3>
      {!r.reportId && <p className="text-sm text-muted">No report with this run.</p>}
      {report.isLoading && <Skeleton variant="card" count={1} />}
      {report.data && (
        <div className="mt-2 min-w-0 rounded-md border border-hair p-3">
          <ReportView doc={report.data.doc} label={`${report.data.period} · ${fmtDate(report.data.windowFrom)} – ${fmtDate(report.data.windowTo)}`} />
          {!report.data.reportEmails.length && <p className="mt-2 text-xs text-warn">No report email set: the client sees it on the dashboard only.</p>}
        </div>
      )}

      <h3 className="mt-4 font-head text-sm text-muted">The ideas</h3>
      {!r.proposalId && <p className="text-sm text-muted">No suggestions with this run.</p>}
      {prop.data && <Ideas p={prop.data} />}

      <div className="mt-4 flex flex-wrap gap-2">
        <Btn kind="primary" disabled={!!busy} onClick={() => setConfirm('release')}>Release</Btn>
        <Btn kind="danger" disabled={!!busy} onClick={() => setConfirm('cancel')}>Cancel the run</Btn>
      </div>
      <ConfirmDialog open={confirm === 'release'} title={`Release ${r.siteName ?? r.slug}'s ${r.period} update?`}
        body="The client sees the report and the ideas now. The monthly note waits in the Outbox for your OK." confirmLabel="Release"
        onConfirm={() => { setConfirm(null); void run('release', () => api.adminReleaseRun(r.id), (x) => `Released. Suggestions are ${x.proposalStatus ?? 'out'}.`); }} onCancel={() => setConfirm(null)} />
      <ConfirmDialog open={confirm === 'cancel'} destructive title="Cancel this run?" body="It ends held: the client sees nothing from it. You can retry it from Monthly runs."
        confirmLabel="Cancel the run" onConfirm={() => { setConfirm(null); void run('cancel', () => api.adminCancelRun(r.id), 'Run canceled.'); }} onCancel={() => setConfirm(null)} />
    </article>
  );
}

export function Ideas({ p, onBehalf }: { p: AdminProposal; onBehalf?: (itemId: string, d: 'approve' | 'decline') => void }) {
  return (
    <ul className="mt-2 flex flex-col gap-2">
      {p.items.map((i) => (
        <li key={i.id} className="min-w-0 rounded-md border border-hair p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs ${CLASS[i.class].tone}`}>{CLASS[i.class].label}</span>
            <span className="min-w-0 break-words font-medium text-ink">{i.title}</span>
          </div>
          <p className="mt-1 break-words text-muted">{i.why}</p>
          <p className="mt-1 text-xs text-muted">{i.commitShas.length} commit{i.commitShas.length === 1 ? '' : 's'} · {i.status}{i.decidedBy ? ` by ${i.decidedBy}` : ''}</p>
          {onBehalf && i.status === 'pending' && p.status === 'published' && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Btn kind="primary" onClick={() => onBehalf(i.id, 'approve')}>Yes, do it (for the client)</Btn>
              <Btn onClick={() => onBehalf(i.id, 'decline')}>No thanks (for the client)</Btn>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
